import { BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';

export const SURVEY_TYPES = Object.freeze({
    NET_PROMOTER_SCORE: 'NET_PROMOTER_SCORE',
    PRODUCT_MARKET_FIT: 'PRODUCT_MARKET_FIT',
    CUSTOMER_SATISFACTION: 'CUSTOMER_SATISFACTION',
    USER_EXPERIENCE_LITE: 'USER_EXPERIENCE_LITE',
});

export const SURVEY_DISPLAY_NAMES = Object.freeze({
    [SURVEY_TYPES.NET_PROMOTER_SCORE]: 'Net Promoter Score',
    [SURVEY_TYPES.PRODUCT_MARKET_FIT]: 'Product Market Fit',
    [SURVEY_TYPES.CUSTOMER_SATISFACTION]: 'Customer Satisfaction',
    [SURVEY_TYPES.USER_EXPERIENCE_LITE]: 'User Experience Lite',
});

export const SURVEY_DISPLAY_ACRONYMS = Object.freeze({
    [SURVEY_TYPES.NET_PROMOTER_SCORE]: 'NPS',
    [SURVEY_TYPES.PRODUCT_MARKET_FIT]: 'PMF',
    [SURVEY_TYPES.CUSTOMER_SATISFACTION]: 'CSAT',
    [SURVEY_TYPES.USER_EXPERIENCE_LITE]: 'UX-Lite',
});

export const ALL_SURVEY_TYPES = Object.freeze([
    { id: 'NPS', label: 'NPS' },
    { id: SURVEY_DISPLAY_ACRONYMS.PRODUCT_MARKET_FIT, label: SURVEY_DISPLAY_ACRONYMS.PRODUCT_MARKET_FIT },
    { id: SURVEY_DISPLAY_ACRONYMS.CUSTOMER_SATISFACTION, label: SURVEY_DISPLAY_ACRONYMS.CUSTOMER_SATISFACTION },
    { id: SURVEY_DISPLAY_ACRONYMS.USER_EXPERIENCE_LITE, label: SURVEY_DISPLAY_ACRONYMS.USER_EXPERIENCE_LITE },
]);

export const SURVEY_SUB_TYPES = Object.freeze({
    UX_LITE_5: '5',
    UX_LITE_7: '7',
});

export const SURVEY_QUESTION_TYPES = Object.freeze({
    OPEN_ANSWER: 'OPEN_ANSWER',
    RATING_SCALE: 'RATING_SCALE',
    SINGLE_CHOICE: 'SINGLE_CHOICE',
    BASIC: 'BASIC',
});

export const SURVEY_QUESTION_TYPE_DETAILS = Object.freeze({
    [SURVEY_QUESTION_TYPES.OPEN_ANSWER]: {
        displayName: 'Open answer',
        iconType: 'open-response',
    },
    [SURVEY_QUESTION_TYPES.RATING_SCALE]: {
        displayName: 'Rating scale',
        iconType: 'likert-scale',
    },
    [SURVEY_QUESTION_TYPES.SINGLE_CHOICE]: {
        displayName: 'Multiple choice',
        iconType: 'single-choice',
    },
    [SURVEY_QUESTION_TYPES.BASIC]: {
        displayName: 'Final',
        iconType: 'success',
    },
});

export const DELIVERY_METHODS = Object.freeze({
    GUIDE: 'GUIDE',
    EMAIL: 'EMAIL',
});

export const GUIDE_POSITION = Object.freeze({
    FOOTER: 'FOOTER',
    OVERLAY: 'OVERLAY',
});

export const GUIDE_LAYOUT = Object.freeze({
    MULTI_STEP: 'MULTI_STEP',
    SINGLE_STEP: 'SINGLE_STEP',
});

export const SURVEY_STATUSES = Object.freeze({
    DISABLED: 'disabled',
    DRAFT: 'draft',
    PENDING_REVIEW: '_pendingReview_',
    STAGED: 'staged',
    PUBLIC: 'public',
    SCHEDULED: 'scheduled',
    DELETE: 'delete',
});

export const SURVEY_STATUS_DISPLAY_NAMES = Object.freeze({
    [SURVEY_STATUSES.DISABLED]: 'Disabled',
    [SURVEY_STATUSES.DRAFT]: 'Draft',
    [SURVEY_STATUSES.PENDING_REVIEW]: 'Pending Review',
    [SURVEY_STATUSES.STAGED]: 'Staged',
    [SURVEY_STATUSES.PUBLIC]: 'Public',
    [SURVEY_STATUSES.SCHEDULED]: 'Scheduled',
    [SURVEY_STATUSES.DELETE]: 'Delete',
});

export const SURVEY_RATING_COLOR_SCALE = [
    'var(--chart-survey-rating-series-1-base)',
    'var(--chart-survey-rating-series-2-base)',
    'var(--chart-survey-rating-series-3-base)',
    'var(--chart-survey-rating-series-4-base)',
    'var(--chart-survey-rating-series-5-base)',
    'var(--chart-survey-rating-series-6-base)',
    'var(--chart-survey-rating-series-7-base)',
];

export const NPS_RATING_COLORS = Object.freeze({
    promoter: 'var(--chart-nps-promoter-base)',
    passive: 'var(--chart-nps-passive-base)',
    detractor: 'var(--chart-nps-detractor-base)',
});

/**
 * Returns the color for a given survey type and value.
 * For most types, value is 1-based and maps to the color scale above.
 * For types with gaps (e.g. CSAT, UMUX), you may need to map values to indices.
 */
export function getSurveyRatingColor (type, value) {
    switch (type) {
        case SURVEY_TYPES.NET_PROMOTER_SCORE:
            if (value >= 9) return NPS_RATING_COLORS.promoter;
            if (value >= 7) return NPS_RATING_COLORS.passive;

            return NPS_RATING_COLORS.detractor;
        case SURVEY_TYPES.PRODUCT_MARKET_FIT:
            return SURVEY_RATING_COLOR_SCALE[[0, 3, 6][value - 1]];
        case SURVEY_TYPES.CUSTOMER_SATISFACTION:
            return {
                1: SURVEY_RATING_COLOR_SCALE[0],
                3: SURVEY_RATING_COLOR_SCALE[2],
                4: SURVEY_RATING_COLOR_SCALE[3],
                5: SURVEY_RATING_COLOR_SCALE[4],
                7: SURVEY_RATING_COLOR_SCALE[6],
            }[value];
        case SURVEY_TYPES.USER_EXPERIENCE_LITE:
            return SURVEY_RATING_COLOR_SCALE[value - 1];
        default:
            // fallback: try value-1 index
            return SURVEY_RATING_COLOR_SCALE[value - 1];
    }
}

export const NEW_SURVEY_ITEM_ID_PREFIX = 'SI-';

export const NEW_SURVEY_ANSWER_ID_PREFIX = 'SA-';

export const { REQUIRED_QUESTION_MARKDOWN, isStepAutoAdvanceEligible } = BuildingBlockLayouts;

export function withoutStepAutoAdvance (step) {
    const { autoAdvance, ...rest } = step.displayAttributes || {};
    if (autoAdvance === undefined) return step;

    const updatedStep = { ...step };

    if (Object.keys(rest).length) {
        updatedStep.displayAttributes = rest;
    } else {
        delete updatedStep.displayAttributes;
    }

    return updatedStep;
}
